// Helper di validazione dei campi VLAN dei Metafield (dialog ciscoSummary).
// Formati attesi:
// - vlan_trunk: numeri separati da virgola senza spazi (es. "10,20,30")
// - vlanlistX (X = 1..10): coppie vlanid,subnet separate da punto e virgola
//   (es. "10,10.0.0.0/24;20,10.0.1.0/24")
// - vlandescrX (X = 1..10): coppie vlanid,descrizione separate da punto e virgola
//   (es. "10,Uplink;20,Voice")

const VLAN_ID_REGEX = /^\d+$/

// IPv4 opzionalmente in CIDR (es. 10.0.0.0 oppure 10.0.0.0/24)
const SUBNET_REGEX = /^(25[0-5]|2[0-4]\d|[01]?\d?\d)(\.(25[0-5]|2[0-4]\d|[01]?\d?\d)){3}(\/(3[0-2]|[0-2]?\d))?$/

const VLANLIST_FIELD_REGEX = /^vlanlist([1-9]|10)$/
const VLANDESCR_FIELD_REGEX = /^vlandescr([1-9]|10)$/

export interface VlanFieldCheck {
  field: string
  value: string
  valid: boolean
  message: string
}

export function isValidVlanTrunk (value: string | null | undefined): boolean {
  if (value == null || value === '') { return true }
  return /^\d+(,\d+)*$/.test(value)
}

export function isValidIpv4Cidr (value: string | null | undefined): boolean {
  if (value == null || value === '') { return false }
  return SUBNET_REGEX.test(String(value).trim())
}

export function isValidVlanList (value: string | null | undefined): boolean {
  if (value == null || value === '') { return true }
  return value.split(';').every(entry => {
    const [vlanId, subnet] = entry.split(',')
    return VLAN_ID_REGEX.test(vlanId) && subnet != null && SUBNET_REGEX.test(subnet)
  })
}

export function isValidVlanDescr (value: string | null | undefined): boolean {
  if (value == null || value === '') { return true }
  return value.split(';').every(entry => {
    const [vlanId, ...rest] = entry.split(',')
    const descrizione = rest.join(',')
    return VLAN_ID_REGEX.test(vlanId) && descrizione !== ''
  })
}

export function isVlanMetafieldName (field: string): boolean {
  return field === 'vlan_trunk' || VLANLIST_FIELD_REGEX.test(field) || VLANDESCR_FIELD_REGEX.test(field)
}

export function validateVlanMetafield (field: string, value: any): VlanFieldCheck {
  const strValue = value == null ? '' : String(value)
  let valid = true
  let message = 'Formato corretto'
  if (strValue === '') {
    message = 'Campo vuoto (consentito)'
  } else if (field === 'vlan_trunk') {
    valid = isValidVlanTrunk(strValue)
    if (!valid) { message = 'Attesi numeri separati da virgola senza spazi (es. 10,20,30)' }
  } else if (VLANLIST_FIELD_REGEX.test(field)) {
    valid = isValidVlanList(strValue)
    if (!valid) { message = 'Attese coppie vlanid,subnet separate da punto e virgola (es. 10,10.0.0.0/24;20,10.0.1.0/24)' }
  } else if (VLANDESCR_FIELD_REGEX.test(field)) {
    valid = isValidVlanDescr(strValue)
    if (!valid) { message = 'Attese coppie vlanid,descrizione separate da punto e virgola (es. 10,Uplink;20,Voice)' }
  }
  return { field, value: strValue, valid, message }
}

export function validateVlanMetafields (record: Record<string, any> | null | undefined): VlanFieldCheck[] {
  if (record == null) { return [] }
  return Object.keys(record)
    .filter(field => isVlanMetafieldName(field))
    .sort()
    .map(field => validateVlanMetafield(field, record[field]))
}

export interface VlanEntry {
  vlanId: string
  detail: string
  detailType: 'trunk' | 'subnet' | 'descrizione'
}

// Divide il valore in voci: vlan_trunk -> una voce per vlanid,
// vlanlistX -> vlanid + subnet, vlandescrX -> vlanid + descrizione.
// Restituisce null se il campo non è valido (o non è un campo VLAN).
export function parseVlanMetafield (field: string, value: any): VlanEntry[] | null {
  const strValue = value == null ? '' : String(value)
  if (strValue === '') { return [] }
  if (field === 'vlan_trunk') {
    if (!isValidVlanTrunk(strValue)) { return null }
    return strValue.split(',').map(vlanId => ({ vlanId, detail: '', detailType: 'trunk' as const }))
  }
  if (VLANLIST_FIELD_REGEX.test(field)) {
    if (!isValidVlanList(strValue)) { return null }
    return strValue.split(';').map(entry => {
      const [vlanId, subnet] = entry.split(',')
      return { vlanId, detail: subnet, detailType: 'subnet' as const }
    })
  }
  if (VLANDESCR_FIELD_REGEX.test(field)) {
    if (!isValidVlanDescr(strValue)) { return null }
    return strValue.split(';').map(entry => {
      const idx = entry.indexOf(',')
      const vlanId = entry.slice(0, idx)
      return { vlanId, detail: entry.slice(idx + 1), detailType: 'descrizione' as const }
    })
  }
  return null
}

// ---------------------------------------------------------------------------
// Tabella VLAN unificata
// ---------------------------------------------------------------------------
// vlan_trunk è l'indice: una riga per ogni VLAN del trunk, arricchita con il
// network preso da tutti i vlanlistX e la descrizione da tutti i vlandescrX.
// Le VLAN presenti solo nelle liste/descrizioni vengono comunque mostrate
// (in coda) e segnalate come "non in trunk".

export type VlanRowStatus = 'ok' | 'warning' | 'error'

export type VlanTableIssueKind =
  | 'trunk-missing'
  | 'list-missing'
  | 'descr-missing'
  | 'invalid-field'
  | 'network-missing'
  | 'description-missing'
  | 'conflicting-value'
  | 'vlan-not-in-trunk'
  | 'duplicate-vlan'

export interface VlanTableIssue {
  kind: VlanTableIssueKind
  severity: 'warning' | 'error'
  message: string
  /** Campo Metafield che ha generato l'anomalia (es. "vlanlist2"). */
  field?: string
  /** VLAN coinvolta, quando l'anomalia riguarda una singola VLAN. */
  vlanId?: string
}

export interface VlanRow {
  vlanId: string
  network: string | null
  description: string | null
  inTrunk: boolean
  status: VlanRowStatus
  issues: VlanTableIssue[]
}

export interface VlanTable {
  rows: VlanRow[]
  /** Anomalie non legate a una singola riga (campi assenti, formati non validi). */
  issues: VlanTableIssue[]
  /** true solo se nessuna riga e nessun campo presenta anomalie. */
  complete: boolean
}

interface VlanValueMap {
  values: Map<string, string>
  conflicts: Set<string>
}

function toVlanText (value: any): string {
  return value == null ? '' : String(value).trim()
}

function vlanFieldIndex (field: string): number {
  const match = /(\d+)$/.exec(field)
  return match ? Number(match[1]) : 0
}

function vlanFieldKeys (fields: Record<string, any>, regex: RegExp): string[] {
  return Object.keys(fields)
    .filter(field => regex.test(field))
    .sort((a, b) => vlanFieldIndex(a) - vlanFieldIndex(b))
}

function splitVlanIds (value: string): string[] {
  return value.split(/[;,\s]+/).map(vlanId => vlanId.trim()).filter(vlanId => vlanId !== '')
}

// Concatena tutte le voci (separatore ';') di un gruppo di campi e le indicizza
// per VLAN. Per vlanlistX il dettaglio è il network, per vlandescrX la
// descrizione (che può contenere virgole: si divide solo sulla prima).
function collectVlanValues (values: string[]): VlanValueMap {
  const map = new Map<string, string>()
  const conflicts = new Set<string>()
  for (const value of values) {
    for (const rawEntry of value.split(';')) {
      const entry = rawEntry.trim()
      if (entry === '') { continue }
      const comma = entry.indexOf(',')
      const vlanId = (comma === -1 ? entry : entry.slice(0, comma)).trim()
      const detail = comma === -1 ? '' : entry.slice(comma + 1).trim()
      if (vlanId === '') { continue }
      const previous = map.get(vlanId)
      if (previous === undefined) {
        map.set(vlanId, detail)
      } else if (previous === '') {
        if (detail !== '') { map.set(vlanId, detail) }
      } else if (detail !== '' && previous !== detail) {
        conflicts.add(vlanId)
      }
    }
  }
  return { values: map, conflicts }
}

function compareVlanIds (a: string, b: string): number {
  const numA = Number(a)
  const numB = Number(b)
  const isNumA = a !== '' && Number.isFinite(numA)
  const isNumB = b !== '' && Number.isFinite(numB)
  if (isNumA && isNumB && numA !== numB) { return numA - numB }
  if (isNumA !== isNumB) { return isNumA ? -1 : 1 }
  return a.localeCompare(b)
}

export function buildVlanTable (fields: Record<string, any> | null | undefined): VlanTable {
  const empty: VlanTable = { rows: [], issues: [], complete: true }
  if (!fields) { return empty }

  const listKeys = vlanFieldKeys(fields, VLANLIST_FIELD_REGEX)
  const descrKeys = vlanFieldKeys(fields, VLANDESCR_FIELD_REGEX)
  const trunkValue = toVlanText(fields['vlan_trunk'])

  const list = collectVlanValues(listKeys.map(key => toVlanText(fields[key])))
  const descr = collectVlanValues(descrKeys.map(key => toVlanText(fields[key])))

  const issues: VlanTableIssue[] = []
  const trunkIds: string[] = []
  const trunkSet = new Set<string>()

  for (const vlanId of splitVlanIds(trunkValue)) {
    if (trunkSet.has(vlanId)) {
      issues.push({
        kind: 'duplicate-vlan',
        severity: 'warning',
        field: 'vlan_trunk',
        vlanId,
        message: `VLAN ${vlanId} duplicata in vlan_trunk.`
      })
      continue
    }
    trunkSet.add(vlanId)
    trunkIds.push(vlanId)
  }

  // Nessun dato VLAN: la sezione non va mostrata.
  if (trunkIds.length === 0 && list.values.size === 0 && descr.values.size === 0) {
    return empty
  }

  if (trunkIds.length === 0) {
    issues.push({
      kind: 'trunk-missing',
      severity: 'error',
      field: 'vlan_trunk',
      message: 'vlan_trunk assente o non leggibile: è l\'indice della tabella VLAN.'
    })
  }
  if (list.values.size === 0) {
    issues.push({
      kind: 'list-missing',
      severity: 'error',
      field: listKeys.join(', ') || undefined,
      message: 'Nessun network disponibile: campi vlanlist assenti o vuoti.'
    })
  }
  if (descr.values.size === 0) {
    issues.push({
      kind: 'descr-missing',
      severity: 'warning',
      field: descrKeys.join(', ') || undefined,
      message: 'Nessuna descrizione disponibile: campi vlandescr assenti o vuoti.'
    })
  }

  for (const check of validateVlanMetafields(fields)) {
    if (check.value !== '' && !check.valid) {
      issues.push({
        kind: 'invalid-field',
        severity: 'error',
        field: check.field,
        message: `${check.field}: ${check.message}`
      })
    }
  }

  const extraIds = [...new Set([...list.values.keys(), ...descr.values.keys()])]
    .filter(vlanId => !trunkSet.has(vlanId))
    .sort(compareVlanIds)

  const rows = [...trunkIds, ...extraIds].map<VlanRow>(vlanId => {
    const rowIssues: VlanTableIssue[] = []
    const inTrunk = trunkSet.has(vlanId)
    const network = list.values.get(vlanId) ?? null
    const description = descr.values.get(vlanId) ?? null

    if (!inTrunk) {
      rowIssues.push({
        kind: 'vlan-not-in-trunk',
        severity: 'error',
        vlanId,
        message: `VLAN ${vlanId} presente in vlanlist/vlandescr ma assente in vlan_trunk.`
      })
    }
    if (network == null || network === '') {
      rowIssues.push({
        kind: 'network-missing',
        severity: 'error',
        vlanId,
        message: `VLAN ${vlanId} senza network nei campi vlanlist.`
      })
    }
    if (description == null || description === '') {
      rowIssues.push({
        kind: 'description-missing',
        severity: 'warning',
        vlanId,
        message: `VLAN ${vlanId} senza descrizione nei campi vlandescr.`
      })
    }
    if (list.conflicts.has(vlanId)) {
      rowIssues.push({
        kind: 'conflicting-value',
        severity: 'warning',
        vlanId,
        message: `VLAN ${vlanId} con network diversi nei campi vlanlist.`
      })
    }
    if (descr.conflicts.has(vlanId)) {
      rowIssues.push({
        kind: 'conflicting-value',
        severity: 'warning',
        vlanId,
        message: `VLAN ${vlanId} con descrizioni diverse nei campi vlandescr.`
      })
    }

    const status: VlanRowStatus = rowIssues.some(issue => issue.severity === 'error')
      ? 'error'
      : rowIssues.length > 0 ? 'warning' : 'ok'

    return { vlanId, network, description, inTrunk, status, issues: rowIssues }
  })

  return {
    rows,
    issues,
    complete: issues.length === 0 && rows.every(row => row.status === 'ok')
  }
}

// ---------------------------------------------------------------------------
// Editing e serializzazione
// ---------------------------------------------------------------------------
// L'editor lavora su righe (id + network + descrizione); al salvataggio i dati
// vengono riscritti nei campi originali:
// - vlan_trunk: "10,20,30"
// - vlanlistX:  "10,10.0.0.0/24;20,10.0.1.0/24"
// - vlandescrX: "10,Uplink;20,Voice"
// Ogni VLAN torna nel campo vlanlist/vlandescr da cui proveniva; le nuove
// righe finiscono nel primo campo disponibile.

export interface VlanEditableRow {
  vlanId: string
  network: string
  description: string
  /** Campo vlanlistX di provenienza, per risalvare nello stesso campo. */
  networkField?: string
  /** Campo vlandescrX di provenienza. */
  descriptionField?: string
}

export interface VlanSerializeResult {
  fields: Record<string, any>
  errors: string[]
}

/** Lunghezza massima del contenuto di un singolo campo vlanlistX / vlandescrX. */
export const VLAN_FIELD_MAX_LENGTH = 255

interface VlanFieldBucket {
  entries: string[]
  length: number
}

function createVlanBuckets (keys: string[]): Map<string, VlanFieldBucket> {
  return new Map(keys.map(key => [key, { entries: [], length: 0 }]))
}

function fitsVlanBucket (bucket: VlanFieldBucket, entry: string): boolean {
  const nextLength = bucket.entries.length === 0 ? entry.length : bucket.length + 1 + entry.length
  return nextLength <= VLAN_FIELD_MAX_LENGTH
}

function addVlanEntry (bucket: VlanFieldBucket, entry: string): void {
  bucket.length = bucket.entries.length === 0 ? entry.length : bucket.length + 1 + entry.length
  bucket.entries.push(entry)
}

// Inserisce la voce nel campo preferito (quello di provenienza); se non entra
// nei 255 caratteri passa ai campi successivi, senza mai troncare la voce.
function placeVlanEntry (
  buckets: Map<string, VlanFieldBucket>,
  keys: string[],
  preferredKey: string,
  entry: string,
  label: string,
  errors: string[]
): void {
  if (entry.length > VLAN_FIELD_MAX_LENGTH) {
    errors.push(`${label} supera i ${VLAN_FIELD_MAX_LENGTH} caratteri e non può essere salvata.`)
    return
  }
  const start = Math.max(keys.indexOf(preferredKey), 0)
  // Prima i campi successivi; se sono tutti pieni si riparte dall'inizio,
  // così non si perde spazio nei campi precedenti.
  for (let offset = 0; offset < keys.length; offset += 1) {
    const key = keys[(start + offset) % keys.length]
    const bucket = buckets.get(key)
    if (bucket && fitsVlanBucket(bucket, entry)) {
      addVlanEntry(bucket, entry)
      return
    }
  }
  const prefix = keys[0].replace(/\d+$/, '')
  errors.push(`${label}: spazio esaurito nei campi ${prefix} (max ${VLAN_FIELD_MAX_LENGTH} caratteri per campo).`)
}

function numberedVlanFields (prefix: string): string[] {
  return Array.from({ length: 10 }, (_, index) => `${prefix}${index + 1}`)
}

function vlanSourceMap (fields: Record<string, any>, keys: string[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const key of keys) {
    for (const entry of parseVlanMetafield(key, fields[key]) ?? []) {
      if (!map.has(entry.vlanId)) {
        map.set(entry.vlanId, key)
      }
    }
  }
  return map
}

/** Righe modificabili a partire dai campi VLAN correnti. */
export function buildVlanDraft (fields: Record<string, any> | null | undefined): VlanEditableRow[] {
  const record = fields ?? {}
  const listSources = vlanSourceMap(record, vlanFieldKeys(record, VLANLIST_FIELD_REGEX))
  const descrSources = vlanSourceMap(record, vlanFieldKeys(record, VLANDESCR_FIELD_REGEX))
  return buildVlanTable(record).rows.map(row => ({
    vlanId: row.vlanId,
    network: row.network ?? '',
    description: row.description ?? '',
    networkField: listSources.get(row.vlanId),
    descriptionField: descrSources.get(row.vlanId)
  }))
}

function preferredVlanField (candidate: string | undefined, keys: string[], fallback: string): string {
  return candidate != null && keys.includes(candidate) ? candidate : fallback
}

/**
 * Serializza le righe modificate nei campi Metafield originali.
 * Restituisce anche gli errori bloccanti (id mancante/duplicato/non numerico,
 * network non valido, separatori non ammessi): se ci sono errori non salvare.
 */
export function serializeVlanFields (
  fields: Record<string, any> | null | undefined,
  rows: VlanEditableRow[]
): VlanSerializeResult {
  const original = fields ?? {}
  const listKeys = numberedVlanFields('vlanlist')
  const descrKeys = numberedVlanFields('vlandescr')
  const defaultListKey = listKeys.find(key => key in original) ?? listKeys[0]
  const defaultDescrKey = descrKeys.find(key => key in original) ?? descrKeys[0]

  const updated: Record<string, any> = { ...original }
  const errors: string[] = []
  const trunkIds: string[] = []
  const seen = new Set<string>()
  const lists = createVlanBuckets(listKeys)
  const descrs = createVlanBuckets(descrKeys)

  for (const row of rows) {
    const vlanId = String(row.vlanId ?? '').trim()
    if (vlanId === '') {
      errors.push('Ogni riga deve avere un VLAN id.')
      continue
    }
    if (!VLAN_ID_REGEX.test(vlanId)) {
      errors.push(`VLAN id "${vlanId}" non valido: usa solo cifre.`)
      continue
    }
    if (seen.has(vlanId)) {
      errors.push(`VLAN ${vlanId} duplicata.`)
      continue
    }
    seen.add(vlanId)
    trunkIds.push(vlanId)

    const network = String(row.network ?? '').trim()
    if (network !== '') {
      if (/[,;]/.test(network)) {
        errors.push(`Network "${network}" (VLAN ${vlanId}) non può contenere virgole o punto e virgola.`)
      } else if (!SUBNET_REGEX.test(network)) {
        errors.push(`Network "${network}" (VLAN ${vlanId}) non valido: atteso IPv4, anche in CIDR.`)
      } else {
        const key = preferredVlanField(row.networkField, listKeys, defaultListKey)
        placeVlanEntry(lists, listKeys, key, `${vlanId},${network}`, `VLAN ${vlanId} (network)`, errors)
      }
    }

    const description = String(row.description ?? '').trim()
    if (description !== '') {
      if (description.includes(';')) {
        errors.push(`Descrizione della VLAN ${vlanId} non può contenere punto e virgola.`)
      } else {
        const key = preferredVlanField(row.descriptionField, descrKeys, defaultDescrKey)
        placeVlanEntry(descrs, descrKeys, key, `${vlanId},${description}`, `VLAN ${vlanId} (descrizione)`, errors)
      }
    }
  }

  updated['vlan_trunk'] = trunkIds.join(',')
  for (const key of listKeys) {
    const bucket = lists.get(key)
    if (bucket && (bucket.entries.length > 0 || key in original)) {
      updated[key] = bucket.entries.join(';')
    }
  }
  for (const key of descrKeys) {
    const bucket = descrs.get(key)
    if (bucket && (bucket.entries.length > 0 || key in original)) {
      updated[key] = bucket.entries.join(';')
    }
  }

  return { fields: updated, errors }
}
